import { NumberFormat } from '@billy/design-system'
import { createContext, PropsWithChildren, ReactElement, useContext, useEffect, useMemo } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { usePeriodDates } from '../../../../../hooks/usePeriodDates'
import { getCurrentLocale } from '../../../../../utils'
import { useSalesReport } from '../../../contexts/salesReportContext'
import { SalesReportDateSelection } from '../../../enums/salesReportDateSelection'
import { useSalesReportFilters } from '../../../hooks/useSalesReportFilters'
import { SalesByValueReportItem } from '../../../types/salesByValueReportItem'
import { SalesReportSummary } from '../../../types/salesReportSummary'
import { getSalesReportDateRange } from '../../../utils/getSalesReportDateRange'
import { GraphValueType } from '../enums/graphValueType'
import { SalesReportGraphBucket } from '../enums/salesReportGraphBucket'
import { SalesReportGraphData } from '../types/salesReportGraphData'
import { SalesReportGraphSummaryValues } from '../types/salesReportGraphSummaryValues'
import { SalesReportStatisticsVariant } from '../types/salesReportStatisticsVariant'
import { formatGraphData } from '../utils/formatGraphData'
import { formatGraphDataByValue } from '../utils/formatGraphDataByValue'

interface ContextState {
  graphBucket?: SalesReportGraphBucket
  graphData: SalesReportGraphData[]
  graphSummary?: SalesReportGraphSummaryValues
  isLoading: boolean
  shouldShowUnpaid: boolean
  variant: SalesReportStatisticsVariant
}

const SalesReportStatisticsContext = createContext<ContextState | undefined>(undefined)

interface SalesReportStatisticsContextProviderProps extends PropsWithChildren {
  isLoading: boolean
  salesReport: SalesByValueReportItem[] | SalesReportSummary[]
  variant: SalesReportStatisticsVariant
}

export const SalesReportStatisticsContextProvider = ({
  children,
  isLoading,
  salesReport,
  variant,
}: SalesReportStatisticsContextProviderProps): ReactElement => {
  const locale = getCurrentLocale()
  const { setSalesReportStatistics } = useSalesReport()
  const [{ dateSelection = SalesReportDateSelection.Issue, period, shouldShowVat }] = useSalesReportFilters()
  const { organization } = useUserOrganization()
  const { fromDate, toDate } = usePeriodDates(period)
  const { startDate, endDate } = getSalesReportDateRange(fromDate, toDate, organization?.firstFiscalYearStart)
  const shouldShowUnpaid = variant !== 'overview' || dateSelection === SalesReportDateSelection.Issue

  const {
    bucket: graphBucket,
    graphData,
    graphSummary,
  } = useMemo(() => {
    if (variant === 'overview') {
      return formatGraphData({
        dateSelection,
        endDate,
        salesReport: salesReport as SalesReportSummary[],
        startDate,
      })
    }

    return {
      bucket: undefined,
      ...formatGraphDataByValue({
        salesReport: salesReport as SalesByValueReportItem[],
      }),
    }
  }, [dateSelection, endDate, salesReport, startDate, variant])

  const filteredGraphSummary = useMemo(() => {
    if (!graphSummary) {
      return
    }

    return {
      ...graphSummary[shouldShowVat ? GraphValueType.Gross : GraphValueType.Net],
      totalAmount: salesReport.length,
    }
  }, [graphSummary, shouldShowVat, salesReport.length])

  const filteredGraphData = useMemo(
    () =>
      graphData.map((graphPoint) => {
        const rawGraphValues = graphPoint.values[shouldShowVat ? GraphValueType.Gross : GraphValueType.Net]
        return {
          ...graphPoint,
          displayValues: rawGraphValues,
          values: {
            paid: Math.max(0, rawGraphValues.paid),
            unpaid: Math.max(0, rawGraphValues.unpaid),
          },
        }
      }),
    [graphData, shouldShowVat],
  )

  const formatSalesReportStatisticsValue = (summaryValue: number) =>
    NumberFormat.getFormattedNumber(summaryValue, locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })

  useEffect(() => {
    if (filteredGraphSummary) {
      setSalesReportStatistics({
        totalPaidValue: formatSalesReportStatisticsValue(filteredGraphSummary.paid),
        totalSalesValue: formatSalesReportStatisticsValue(filteredGraphSummary.total),
        totalUnpaidValue: formatSalesReportStatisticsValue(filteredGraphSummary.unpaid),
      })
    }
    // Update statistics in parent context for use in export functionality
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when the summary changes; the formatter is redefined every render, so depending on it would fire this on every render
  }, [filteredGraphSummary])

  return (
    <SalesReportStatisticsContext.Provider
      value={{
        graphBucket,
        graphData: filteredGraphData,
        graphSummary: filteredGraphSummary,
        isLoading,
        shouldShowUnpaid,
        variant,
      }}
    >
      {children}
    </SalesReportStatisticsContext.Provider>
  )
}

export const useSalesReportStatistics = () => {
  const context = useContext(SalesReportStatisticsContext)

  if (!context) {
    throw new Error('SalesReportStatisticsContextProvider is missing in the module!')
  }

  return context
}
